import { ReactNode, useEffect } from "react"
import { Draft } from "immer"
import { useImmerReducer } from "use-immer"
import { useUpdateEffect } from "react-use"
import { ComboboxAction, ComboboxState, RootContextProps } from "./types"
import { createContext } from "../utils"
import { styled } from "../styles"
import { boxCss } from "../Box"

const [Provider, useCombobox] = createContext<RootContextProps>(`Combobox`)

type ComboboxOwnProps = Readonly<{
  items: ReadonlyArray<string>
  defaultOpen?: boolean
  defaultValue?: string
  onOpenChange?: (open: boolean) => void
  children: ReactNode
}>

export type ComboboxProps = ComboboxOwnProps

const Root: React.FC<ComboboxProps> = ({ items, defaultOpen, defaultValue, children, onOpenChange }: ComboboxProps) => {
  const [state, dispatch] = useImmerReducer<ComboboxState, ComboboxAction>(reducer, {
    items,
    open: defaultOpen ?? false,
    selectedItem: defaultValue ?? null,
  })

  useEffect(() => {
    if (defaultValue !== undefined) {
      dispatch({ type: `selectItem`, item: defaultValue })
    }
  }, [defaultValue, dispatch])

  useUpdateEffect(() => {
    onOpenChange?.(state.open)
  }, [state.open])

  useUpdateEffect(() => {
    dispatch({
      type: `setItems`,
      items,
    })
  }, [items])

  return (
    <StyledContainer>
      <Provider state={state} dispatch={dispatch}>
        {children}
      </Provider>
    </StyledContainer>
  )
}

const StyledContainer = styled.div({
  ...boxCss,
  position: `relative`,
  display: `inline-block`,
  width: `100%`,
})

function reducer(state: Draft<ComboboxState>, action: ComboboxAction): ComboboxState {
  switch (action.type) {
    case `selectItem`:
      return { ...state, selectedItem: action.item }
    case `setItems`:
      return { ...state, items: action.items }
    case `setInputValue`:
      return { ...state, inputValue: action.value }
    case `setOpen`:
      return { ...state, open: action.open }
    default:
      throw new Error(`No case exists for ${action}`)
  }
}

Root.displayName = `ComboboxRoot`

export { Root, useCombobox }
